import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";

const AuthContext = createContext();

// Decodifica JWT para obtener payload (sin verificar firma)
const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

// Verifica si el token ha expirado
const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 < Date.now();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);

  // Re-autenticación silenciosa con credenciales existentes
  const silentReauth = useCallback(async (name, phone) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/quick-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setUser(result.user);
        setToken(result.token);
        localStorage.setItem("auth", JSON.stringify({ user: result.user, token: result.token }));
        localStorage.setItem('userData', JSON.stringify({
          id: result.user._id,
          name: result.user.name,
          phone: result.user.phone
        }));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  // Cargar datos desde localStorage al iniciar
  useEffect(() => {
    const loadAuth = async () => {
      try {
        const stored = localStorage.getItem("auth");
        if (stored) {
          const { user: storedUser, token: storedToken } = JSON.parse(stored);

          if (storedToken && !isTokenExpired(storedToken)) {
            setUser(storedUser);
            setToken(storedToken);
          } else if (storedUser?.name && storedUser?.phone) {
            // Token expirado → intentar re-autenticación silenciosa
            const success = await silentReauth(storedUser.name, storedUser.phone);
            if (!success) {
              localStorage.removeItem("auth");
              localStorage.removeItem("userData");
            }
          } else {
            // No hay datos suficientes para re-autenticar
            localStorage.removeItem("auth");
          }
        }
      } catch (error) {
        console.error("Error loading auth from localStorage:", error);
        localStorage.removeItem("auth");
      } finally {
        setIsHydrated(true);
        setLoading(false);
      }
    };

    loadAuth();
  }, [silentReauth]);

  // Guardar en localStorage cuando cambie el estado (solo después de hidratación)
  useEffect(() => {
    if (!isHydrated) return;
    if (user || token) {
      localStorage.setItem("auth", JSON.stringify({ user, token }));
    }
  }, [user, token, isHydrated]);

  // Escuchar evento auth:expired (disparado por authFetch cuando detecta token expirado)
  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(null);
      setToken(null);
    };
    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, []);

  // Quick register - registro rápido con nombre y teléfono
  const quickRegister = useCallback(async (name, phone, address = null) => {
    try {
      const body = { name, phone };
      if (address) {
        body.address = address;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/quick-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setUser(result.user);
        setToken(result.token);

        // Guardar inmediatamente en localStorage (no esperar al useEffect)
        localStorage.setItem("auth", JSON.stringify({ user: result.user, token: result.token }));

        // También guardar en formato antiguo para compatibilidad
        localStorage.setItem('userData', JSON.stringify({
          id: result.user._id,
          name: result.user.name,
          phone: result.user.phone
        }));

        // Toast de bienvenida
        toast.success(result.isNewUser ? '¡Bienvenido!' : '¡Hola de nuevo!');

        return { success: true, user: result.user, token: result.token, isNewUser: result.isNewUser };
      } else {
        toast.error(result.message || 'Error en registro');
        throw new Error(result.message || 'Error en registro');
      }
    } catch (error) {
      console.error("Error en quickRegister:", error);
      toast.error('Error de conexión. Intenta de nuevo.');
      return { success: false, error: error.message };
    }
  }, []);

  // Refresh token - para llamar desde componentes que detecten un 401
  const refreshToken = useCallback(async () => {
    if (!user?.name || !user?.phone) return false;
    setLoading(true);
    try {
      return await silentReauth(user.name, user.phone);
    } finally {
      setLoading(false);
    }
  }, [user, silentReauth]);

  // Logout - limpia estado y localStorage
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("auth");
    localStorage.removeItem("userData");
    localStorage.removeItem("cartItems");
    localStorage.removeItem("userAddress");
    localStorage.removeItem("userAddresses");
    localStorage.removeItem("appliedCoupon");
    localStorage.removeItem("usedCoupons");
    // Disparar evento para actualizar el contador del carrito en el header
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  }, []);

  // Verificar si está autenticado (tiene token válido)
  const isAuthenticated = token !== null && !isTokenExpired(token);

  // Actualizar datos del usuario
  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated,
    quickRegister,
    refreshToken,
    logout,
    updateUser
  }), [user, token, loading, isAuthenticated, quickRegister, refreshToken, logout, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
